import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Newspaper, Filter, TrendingUp, Bookmark, Sparkles, ArrowRight, Users, Globe, Zap, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function HeroSection() {
  const features = [
    {
      icon: Newspaper,
      title: 'Personalized Feed',
      description: 'Curated news from sources you trust'
    },
    {
      icon: Filter,
      title: 'Smart Filters',
      description: 'Find exactly what matters to you'
    },
    {
      icon: TrendingUp,
      title: 'Top Stories',
      description: 'Stay updated with trending news'
    },
    {
      icon: Bookmark,
      title: 'Save for Later',
      description: 'Bookmark articles to read anytime'
    }
  ]

  return (
    <div className="bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium mb-6"
          >
            <Sparkles className="w-4 h-4" />
            Your Personalized News Hub
          </motion.div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Stay Informed and Support
            <span className="block mt-2">
              <span data-logo-text className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-slate-600 dark:from-blue-400 dark:to-slate-400">
                Real, Independent Journalism
              </span>
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-8">
            Get personalized news from trusted sources, all in one place.
            Filter by topic, save favorites, and never miss what matters.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/signup">
              <Button size="lg" className="group">
                Get Started Free
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline">
                View Plans
              </Button>
            </Link>
          </div>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 dark:border-gray-700 text-center"
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-50 to-slate-50 dark:from-blue-900/30 dark:to-slate-900/30 flex items-center justify-center mb-4 mx-auto">
                  <Icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Trust Metrics - Professional */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {/* Active Readers */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">10K+</div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Readers</p>
            </div>

            {/* News Sources */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-lg bg-slate-50 dark:bg-slate-900/20 flex items-center justify-center">
                  <Newspaper className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">500+</div>
              <p className="text-sm text-gray-600 dark:text-gray-400">News Sources</p>
            </div>

            {/* Real-time Updates */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">24/7</div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Real-time Updates</p>
            </div>

            {/* Countries Served */}
            <div className="text-center">
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">50+</div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Countries</p>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-2 mt-8 text-sm text-gray-500 dark:text-gray-400">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span>Trusted by thousands of readers worldwide</span>
          </div>
        </motion.div>
      </div>
    </div >
  )
}

